import React from 'react'
import { Cpu, Monitor, Zap, Grid3X3, Shield } from 'lucide-react'
import type { LEDProcessor, LEDPanel, PowerCabinet, WorkspaceElement } from '@/types/synoptique'

interface DraggedItem {
  type: 'processor' | 'panel' | 'cabinet' | 'image'
  data: LEDProcessor | LEDPanel | PowerCabinet | WorkspaceElement
}

interface TauriDragOverlayProps {
  draggedItem: DraggedItem | null
  dragMousePosition: { x: number; y: number } | null
  getProcessorImageUrl: (processor: LEDProcessor) => string
  isDraggingBackupButton: boolean
  backupButtonDragMousePosition: { x: number; y: number } | null
  processorForBackup: string | null
  t: (key: string) => string
}

const TauriDragOverlay = React.memo(function TauriDragOverlay({
  draggedItem,
  dragMousePosition,
  getProcessorImageUrl,
  isDraggingBackupButton,
  backupButtonDragMousePosition,
  processorForBackup,
  t,
}: TauriDragOverlayProps) {
  return (
    <>
      {/* Overlay personnalisé pour Tauri - Aperçu du drag */}
      {draggedItem && dragMousePosition && (
        <div
          className="fixed pointer-events-none z-50 opacity-80"
          style={{
            left: `${dragMousePosition.x}px`,
            top: `${dragMousePosition.y}px`,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <div className="bg-card border-2 border-primary rounded-lg shadow-lg p-3 max-w-[200px]">
            {draggedItem.type === 'processor' && (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <Cpu className="h-4 w-4 text-primary" />
                  <span className="font-medium text-sm">{(draggedItem.data as LEDProcessor).model}</span>
                </div>
                <p className="text-xs text-muted-foreground">{(draggedItem.data as LEDProcessor).brand}</p>
                {(draggedItem.data as LEDProcessor).image && (
                  <img
                    src={getProcessorImageUrl(draggedItem.data as LEDProcessor)}
                    alt={(draggedItem.data as LEDProcessor).model}
                    className="mt-2 w-full h-auto rounded border"
                    style={{ maxHeight: '100px', objectFit: 'contain' }}
                  />
                )}
              </>
            )}
            {draggedItem.type === 'panel' && (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <Monitor className="h-4 w-4 text-primary" />
                  <span className="font-medium text-sm">{(draggedItem.data as LEDPanel).name}</span>
                </div>
                <p className="text-xs text-muted-foreground">{(draggedItem.data as LEDPanel).brand}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {(draggedItem.data as LEDPanel).resolution.width}×{(draggedItem.data as LEDPanel).resolution.height}
                </p>
              </>
            )}
            {draggedItem.type === 'cabinet' && (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="h-4 w-4 text-primary" />
                  <span className="font-medium text-sm">{(draggedItem.data as PowerCabinet).name}</span>
                </div>
                <p className="text-xs text-muted-foreground">{(draggedItem.data as PowerCabinet).brand}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {(draggedItem.data as PowerCabinet).maxPower}W - {(draggedItem.data as PowerCabinet).outlets} prises
                </p>
              </>
            )}
            {draggedItem.type === 'image' && (() => {
              const mireEl = draggedItem.data as WorkspaceElement
              const mireConfig = mireEl.data?.mireConfig
              return (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <Grid3X3 className="h-4 w-4 text-primary" />
                    <span className="font-medium text-sm">{mireConfig?.name || 'Mire'}</span>
                  </div>
                  {mireConfig?.totalResolution && (
                    <p className="text-xs text-muted-foreground">
                      {mireConfig.totalResolution.width}×{mireConfig.totalResolution.height}px
                    </p>
                  )}
                  {mireEl.data?.capturedImage && (
                    <img src={mireEl.data.capturedImage} alt="mire" className="mt-2 w-full h-auto rounded border" style={{ maxHeight: '80px', objectFit: 'cover' }} />
                  )}
                </>
              )
            })()}
          </div>
        </div>
      )}

      {/* Overlay visuel pour le drag du bouton backup */}
      {isDraggingBackupButton && backupButtonDragMousePosition && processorForBackup && (
        <div
          className="fixed pointer-events-none z-50 flex items-center justify-center"
          style={{
            left: `${backupButtonDragMousePosition.x}px`,
            top: `${backupButtonDragMousePosition.y}px`,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <div
            className="flex items-center justify-center rounded-lg px-4 py-3 shadow-xl border-2 bg-background/95 backdrop-blur-sm"
            style={{
              borderColor: 'hsl(var(--primary))',
              boxShadow: '0 0 25px hsl(var(--primary) / 0.5), 0 4px 12px rgba(0,0,0,0.3)'
            }}
          >
            <Shield className="h-5 w-5 mr-2 text-primary" />
            <span className="text-sm font-bold text-primary">
              {t('synoptic.iWillBeYourBackup') || 'I will be your backup'}
            </span>
          </div>
        </div>
      )}
    </>
  )
})

export default TauriDragOverlay
